import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { toFormString } from '@/helpers';

import { Textarea } from '../Textarea';

type MissedCommentFormValues = {
  missed_comment: string;
};

export type MissedCommentDialogMode = 'mark' | 'edit';

export type MissedCommentDialogProps = {
  mode: MissedCommentDialogMode;
  defaultComment: string | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (missedComment: string) => void;
};

export const MissedCommentDialog = (props: MissedCommentDialogProps) => {
  const { mode, defaultComment, isPending, onClose, onConfirm } = props;
  const { t } = useTranslation();

  const isEdit = mode === 'edit';

  const { register, handleSubmit } = useForm<MissedCommentFormValues>({
    defaultValues: { missed_comment: toFormString(defaultComment) },
  });

  const onSubmit = handleSubmit((values) =>
    onConfirm(values.missed_comment.trim()),
  );

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => !isOpen && !isPending && onClose()}
    >
      <DialogContent>
        <form
          onSubmit={onSubmit}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>
              {isEdit ? t('Edit no-show comment') : t('Mark no-show')}
            </DialogTitle>
            <DialogDescription>
              {t('Why did the client not come? The comment is optional.')}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            label={t('No-show comment')}
            placeholder={t('For example: the client was sick')}
            rows={4}
            autoFocus
            {...register('missed_comment')}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
            >
              {isEdit ? t('Save') : t('Mark no-show')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
